import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { LocalUserChoices } from '@livekit/components-react';
import { PageClientProps } from '../types/types';
import { VideoConferenceComponent } from '../components/VideoConferenceComponent';
import { useAppStore } from 'store/appStore';
import { getUserName, PreJoin, UserChoices } from './PreJoin';
import { isCustomMeeting } from './lib/utils';
import CircularProgress from '@mui/joy/CircularProgress';

export function RoomComponent(props: PageClientProps) {
  const { roomName, hq, codec, interviewId } = props;
  const getConnectionDetails = useAppStore(state => state.getConnectionDetails);

  const [preJoinChoices, setPreJoinChoices] = useState<LocalUserChoices | undefined>(undefined);
  const [connectionDetails, setConnectionDetails] = useState<
    | {
        participantToken: string;
        serverUrl: string;
      }
    | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);

  const isVideoOptional = useMemo(() => {
    return isCustomMeeting(interviewId);
  }, [interviewId]);

  const preJoinDefaults = useMemo(() => {
    return {
      firstName: '',
      lastName: '',
      email: '',
      videoEnabled: isVideoOptional ? false : true,
      audioEnabled: true,
    };
  }, [isVideoOptional]);

  const handlePreJoinSubmit = useCallback(
    async (values: UserChoices) => {
      setLoading(true);
      setPreJoinChoices({
        videoEnabled: values.videoEnabled!,
        audioEnabled: values.audioEnabled!,
        videoDeviceId: values.videoDeviceId!,
        audioDeviceId: values.audioDeviceId!,
        username: getUserName(values.firstName, values.lastName),
      });

      try {
        const connectionDetails = await getConnectionDetails({
          roomName: roomName,
          participantName: getUserName(values.firstName, values.lastName),
          metadata: {
            interviewId: interviewId,
            interviewee: {
              firstName: values.firstName,
              lastName: values.lastName,
              email: values.email,
            },
          },
        });

        setConnectionDetails(connectionDetails);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching connection details:', error);
        setLoading(false);
      }
    },
    [roomName, getConnectionDetails, interviewId]
  );

  return (
    <main style={{ height: '100%' }} data-lk-theme="default">
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <CircularProgress />
          <span style={{ marginLeft: 12 }}>Joining...</span>
        </div>
      ) : connectionDetails === undefined || preJoinChoices === undefined ? (
        <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
          <PreJoin 
            defaults={preJoinDefaults} 
            onSubmit={handlePreJoinSubmit} 
            isVideoOptional={isVideoOptional}
            interviewId={interviewId}
          />
        </div>
      ) : (
        <VideoConferenceComponent
          connectionDetails={connectionDetails}
          userChoices={preJoinChoices}
          options={{ codec, hq }}
        />
      )}
    </main>
  );
}